<script setup lang="ts">
import type proceedButton from '@/components/auth/verification/Box/Proceed.vue';
import type inputFullPhone from '@/components/base/form/InputFullPhone.vue';

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { fetchUserProfile } = useUserData();
const { isCaptchaRegisterEnabled } = useCaptcha();
const { isSmsIntegrationEnabled } = useFeatureFlagsJson();
const { userPhoneDdi, userPhone } = useUserData();

const isLoading = ref(false);
const proceedButtonRef = ref<InstanceType<typeof proceedButton> | null>(null);
const inputPhoneRef = ref<InstanceType<typeof inputFullPhone> | null>(null);

const antiRobotToken = computed<string>(
  () => proceedButtonRef.value?.antiRobotToken ?? '',
);

const selectedPhoneNumber = computed<string>(() => {
  return inputPhoneRef.value?.selectedPhoneNumber ?? '';
});

const selectedPhoneDdi = computed<string>(() => {
  return inputPhoneRef.value?.selectedPhoneDdi ?? '';
});

const isProceedDisabled = computed(() => {
  if (isCaptchaRegisterEnabled.value && !antiRobotToken.value) {
    return true;
  }

  return !selectedPhoneDdi.value || !selectedPhoneNumber.value;
});

async function onPhoneFormSubmit() {
  if (isProceedDisabled.value) return;

  const { ApiService } = useApiService();

  isLoading.value = true;

  await ApiService.patch('/bff/users/update-pending-data', {
    phone: selectedPhoneNumber.value,
    ddi: selectedPhoneDdi.value,
  })
    .then(async (res) => {
      await fetchUserProfile({ force: true });

      openToaster({
        message: t('account.phone_section.update_success'),
        type: 'success',
      });

      if (!isSmsIntegrationEnabled.value) {
        await $containment.solve({
          name: 'phone',
          customValidator: () => res.data.success,
        });
      }
    })
    .catch((error) => {
      const errorMessage =
        error.data.message || t('account.phone_section.update_error');

      openToaster({
        message: errorMessage,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

const phoneInfoMissing = computed(() => {
  if (!userPhoneDdi.value && !userPhone.value) {
    return t('account.phone_section.ddi_and_phone');
  }

  if (!userPhoneDdi.value) {
    return t('account.phone_section.ddi');
  }

  return t('account.phone_section.phone_numer');
});
</script>

<template>
  <AuthVerificationBox :is-loading="isLoading">
    <div
      v-if="(!userPhoneDdi || !userPhone) && isSmsIntegrationEnabled"
      class="alert alert-warning text-xs mb-4"
    >
      <span
        v-html="
          $t('account.phone_section.phone_info_missing', {
            infoMissing: phoneInfoMissing,
          })
        "
      />
    </div>

    <form class="registerFields px-0 md:px-0">
      <label
        class="registerLabel"
        for="phone"
      >
        {{ $t('account.phone') }}
      </label>

      <BaseFormInputFullPhone
        id="phone"
        ref="inputPhoneRef"
        :required="true"
      />

      <AuthVerificationBoxProceed
        ref="proceedButtonRef"
        :disabled="isProceedDisabled"
        @submit="onPhoneFormSubmit"
      />
    </form>
  </AuthVerificationBox>
</template>
